import React, { useEffect, useState } from 'react';
import { useParams, Navigate } from 'react-router-dom';
import AppShell from '../components/AppShell';
import PasscodeGate, { isUnlocked } from '../components/PasscodeGate';
import { MEETING_SLUGS, brandingForSeriesName, meetingFromEvent } from '../data/meetings';
import { fetchEventBySlug, listPortalEvents, peekLive } from '../data/live';
import Skeleton from '../components/Skeleton';

// Old/short links like goihes.com/cpes point at the council, not a specific
// meeting. Send them to that council's next (or most recent) live meeting.
async function resolveCouncilSlug(councilSlug) {
  const events = (await listPortalEvents()) || [];
  const today = new Date().toISOString().slice(0, 10);
  const mine = events.filter(
    (ev) => ev.portal_slug && brandingForSeriesName(ev.meeting_series?.name).slug === councilSlug,
  );
  const next = mine.find((ev) => (ev.end_date || ev.start_date || '') >= today);
  return (next || mine[mine.length - 1])?.portal_slug || null;
}

export default function MeetingLayout() {
  const { slug } = useParams();

  // { slug, meeting } once the event row loads, or { slug, redirect } when the
  // slug isn't a live meeting. Nothing renders from static sample data.
  // Seeded from the saved copy so a return visit (or no signal) opens instantly.
  const [resolved, setResolved] = useState(() => {
    const saved = peekLive(fetchEventBySlug, [slug]);
    return saved ? { slug, meeting: meetingFromEvent(saved) } : null;
  });
  // Passcode accepted this session (covers browsers where storage is blocked).
  const [unlockedSlug, setUnlockedSlug] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const event = await fetchEventBySlug(slug);
      if (event) {
        if (!cancelled) setResolved({ slug, meeting: meetingFromEvent(event) });
        return;
      }
      const target = MEETING_SLUGS.includes(slug) ? await resolveCouncilSlug(slug) : null;
      if (!cancelled) setResolved({ slug, redirect: target ? `/${target}` : '/' });
    })();
    return () => { cancelled = true; };
  }, [slug]);

  // Ignore a result left over from the previous slug (e.g. jumping between
  // meetings from Upcoming) so its content or unlock never carries over.
  const current = resolved?.slug === slug ? resolved : null;

  if (!current) {
    return (
      <div className="portal-loading">
        <Skeleton variant="card" count={3} />
      </div>
    );
  }
  if (current.redirect) return <Navigate to={current.redirect} replace />;

  const { meeting } = current;

  // Every entry point (landing card, shared deep link, installed app) passes
  // through here, so the gate also covers direct links like /cpes_2026/attendees.
  const passcode = meeting._event?.portal_passcode;
  if (passcode && unlockedSlug !== slug && !isUnlocked(slug, passcode)) {
    return (
      <PasscodeGate
        meeting={meeting}
        passcode={passcode}
        onUnlock={() => setUnlockedSlug(slug)}
      />
    );
  }

  const themeSlug = meeting.themeSlug || meeting.slug;

  return (
    <div data-meeting={themeSlug} className="portal-root">
      <AppShell meeting={meeting} />
    </div>
  );
}
